import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, Clock, Shield, Users, BarChart3, Zap, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { API } from '../config/apiBase';


const PLAN_COLORS = {
    free: '#6B7280', lite: '#10B981', start: '#F59E0B',
    gestao: '#3B82F6', pro: '#8B5CF6', enterprise: '#2DD4BF',
};

export default function InviteOnboardingPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [state, setState] = useState('loading'); // loading | valid | used | expired | invalid | success
    const [invite, setInvite] = useState(null);
    const [form, setForm] = useState({ empresa_name: '', nome_fantasia: '', empresa_email: '', owner_name: '', owner_email: '', owner_phone: '', password: '', password_confirm: '' });
    const [agree, setAgree] = useState(false);
    const [newsletter, setNewsletter] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (!token) { setState('invalid'); return; }
        fetch(`${API}/public/empresa-invite/${token}`)
            .then(r => r.json())
            .then(data => {
                if (data.valid) {
                    setInvite(data.invite);
                    setForm(f => ({
                        ...f,
                        empresa_name: data.invite.empresa_nome_prevista || '',
                        owner_name: data.invite.responsavel_nome_previsto || '',
                        owner_email: data.invite.responsavel_email_previsto || '',
                    }));
                    setState('valid');
                } else {
                    setState(data.reason || 'invalid');
                }
            })
            .catch(() => setState('invalid'));
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!form.empresa_name.trim()) { setError('Nome da empresa obrigatorio'); return; }
        if (!form.owner_name.trim()) { setError('Nome do responsavel obrigatorio'); return; }
        if (!form.owner_email.trim()) { setError('Email de acesso obrigatorio'); return; }
        if (!form.password || form.password.length < 6) { setError('Senha deve ter pelo menos 6 caracteres'); return; }
        if (form.password !== form.password_confirm) { setError('Senhas nao conferem'); return; }
        if (!agree) { setError('Voce deve concordar com os termos'); return; }
        setSubmitting(true);
        try {
            const res = await fetch(`${API}/public/empresa-invite/${token}/claim`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    empresa_name: form.empresa_name,
                    nome_fantasia: form.nome_fantasia,
                    empresa_email: form.empresa_email || form.owner_email,
                    owner_name: form.owner_name,
                    owner_email: form.owner_email,
                    owner_phone: form.owner_phone,
                    password: form.password,
                }),
            });
            const data = await res.json();
            if (res.ok && data.ok) {
                setResult(data);
                if (data.token) {
                    localStorage.setItem('token', data.token);
                }
                setState('success');
            } else {
                setError(data.detail || 'Erro ao criar empresa');
            }
        } catch { setError('Erro de conexao'); }
        setSubmitting(false);
    };

    const planColor = invite ? (PLAN_COLORS[invite.plano] || '#3D8BFF') : '#3D8BFF';

    // Loading
    if (state === 'loading') {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: '#071A3A' }}>
                <div className="text-center" data-testid="invite-loading">
                    <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'var(--sv-brand-icon-bg)', boxShadow: '0 0 40px rgba(var(--sv-accent-rgb), 0.2)' }}>
                        <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 28, color: 'var(--sv-brand-icon-fg)' }}>S</span>
                    </div>
                    <Loader2 size={20} className="animate-spin mx-auto mb-3" style={{ color: 'var(--sv-accent)' }} />
                    <p className="text-sm" style={{ color: '#888' }}>Validando seu convite...</p>
                </div>
            </div>
        );
    }

    // Error states
    if (state === 'expired' || state === 'used' || state === 'invalid' || state === 'revoked') {
        const msgs = {
            expired: { icon: Clock, title: 'Este convite expirou', text: 'O prazo para usar este link foi encerrado. Solicite um novo convite ao administrador.' },
            used: { icon: CheckCircle, title: 'Este convite ja foi utilizado', text: 'Esse link era de uso unico e ja foi consumido em um cadastro concluido.' },
            invalid: { icon: XCircle, title: 'Convite invalido', text: 'Nao encontramos um convite valido para este link.' },
            revoked: { icon: Shield, title: 'Convite revogado', text: 'Este convite foi revogado pelo administrador.' },
        };
        const m = msgs[state] || msgs.invalid;
        return (
            <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#071A3A' }}>
                <div className="w-full max-w-md text-center rounded-2xl p-8" style={{ background: '#0B214A', border: '1px solid rgba(125,165,255,0.16)' }} data-testid={`invite-${state}`}>
                    <div className="w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center" style={{ background: state === 'used' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)' }}>
                        <m.icon size={24} style={{ color: state === 'used' ? '#10B981' : '#EF4444' }} />
                    </div>
                    <h1 className="text-xl font-bold mb-2" style={{ color: '#F5F5F5' }}>{m.title}</h1>
                    <p className="text-sm mb-6" style={{ color: '#888' }}>{m.text}</p>
                    <div className="flex gap-2 justify-center">
                        {state === 'used' && (
                            <button onClick={() => navigate('/login')} className="px-5 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="go-login">
                                Ir para login
                            </button>
                        )}
                        <button onClick={() => navigate('/')} className="px-5 py-2.5 rounded-xl text-sm font-medium" style={{ background: '#0D2C63', color: '#888', border: '1px solid rgba(125,165,255,0.16)' }} data-testid="go-home">
                            Voltar para o site
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Success
    if (state === 'success') {
        return (
            <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#071A3A' }}>
                <div className="w-full max-w-md text-center rounded-2xl p-8" style={{ background: '#0B214A', border: '1px solid rgba(125,165,255,0.16)' }} data-testid="invite-success">
                    <div className="w-16 h-16 rounded-xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' }}>
                        <CheckCircle size={28} style={{ color: '#10B981' }} />
                    </div>
                    <h1 className="text-xl font-bold mb-2" style={{ color: '#F5F5F5' }}>Empresa criada com sucesso</h1>
                    <p className="text-sm mb-4" style={{ color: '#888' }}>Seu acesso foi ativado e o convite foi encerrado com sucesso.</p>
                    <div className="rounded-xl p-4 mb-6 text-left space-y-2" style={{ background: '#071A3A', border: '1px solid rgba(125,165,255,0.16)' }}>
                        <div className="flex justify-between text-xs"><span style={{ color: '#666' }}>Empresa</span><span style={{ color: '#F5F5F5' }}>{form.empresa_name}</span></div>
                        <div className="flex justify-between text-xs"><span style={{ color: '#666' }}>Plano</span><span className="font-semibold" style={{ color: planColor }}>{invite?.plan_name}</span></div>
                        <div className="flex justify-between text-xs"><span style={{ color: '#666' }}>Responsavel</span><span style={{ color: '#F5F5F5' }}>{form.owner_name}</span></div>
                    </div>
                    <div className="flex gap-2 justify-center">
                        <button onClick={() => navigate('/sistema')} className="px-5 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="go-panel">
                            Ir para o painel
                        </button>
                        <button onClick={() => navigate('/login')} className="px-5 py-2.5 rounded-xl text-sm font-medium" style={{ background: '#0D2C63', color: '#888', border: '1px solid rgba(125,165,255,0.16)' }} data-testid="go-login-success">
                            Ir para login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Valid invite — main form
    return (
        <div className="min-h-screen" style={{ background: '#071A3A' }}>
            <div className="max-w-[1100px] mx-auto p-4 sm:p-6 lg:p-8">
                {/* Header */}
                <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--sv-brand-icon-bg)' }}>
                        <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 20, color: 'var(--sv-brand-icon-fg)' }}>S</span>
                    </div>
                    <span className="text-sm font-bold tracking-widest uppercase" style={{ color: 'var(--sv-accent)', fontFamily: "var(--font-display)" }}>SuperVitrine</span>
                </div>

                <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
                    {/* Left column — presentation */}
                    <div className="lg:col-span-2 space-y-4">
                        <div className="rounded-2xl p-6" style={{ background: '#0B214A', border: '1px solid rgba(125,165,255,0.16)' }}>
                            <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full mb-4" style={{ background: `${planColor}15`, color: planColor, border: `1px solid ${planColor}30` }}>
                                Convite liberado
                            </span>
                            <h1 className="text-2xl sm:text-3xl font-bold mb-3 leading-tight" style={{ color: '#F5F5F5' }}>
                                Seu acesso a SuperVitrine ja esta liberado
                            </h1>
                            <p className="text-sm leading-relaxed" style={{ color: '#888' }}>
                                Conclua o cadastro da sua empresa para ativar seu plano sem precisar realizar pagamento.
                            </p>
                        </div>

                        {/* Plan info */}
                        <div className="rounded-2xl p-5" style={{ background: '#0B214A', border: '1px solid rgba(125,165,255,0.16)' }}>
                            <div className="flex items-center gap-2 mb-3">
                                <Zap size={14} style={{ color: planColor }} />
                                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: planColor }}>Plano {invite?.plan_name}</span>
                            </div>
                            <div className="space-y-2">
                                {(invite?.plan_features || []).map((f, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                        <CheckCircle size={12} style={{ color: '#22c55e' }} />
                                        <span className="text-xs" style={{ color: '#ccc' }}>{f}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Meta */}
                        <div className="rounded-2xl p-5 space-y-3" style={{ background: '#0B214A', border: '1px solid rgba(125,165,255,0.16)' }}>
                            <div className="flex items-center justify-between text-xs">
                                <span style={{ color: '#666' }}>Validade do convite</span>
                                <span style={{ color: '#F59E0B' }}>{invite?.expires_at ? new Date(invite.expires_at).toLocaleString('pt-BR') : '-'}</span>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                                <span style={{ color: '#666' }}>Tipo</span>
                                <span style={{ color: '#888' }}>Convite unico</span>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                                <span style={{ color: '#666' }}>Cobranca</span>
                                <span className="font-medium" style={{ color: '#10B981' }}>Isenta</span>
                            </div>
                        </div>

                        {/* Welcome message */}
                        {invite?.welcome_message && (
                            <div className="rounded-2xl p-5" style={{ background: '#0B214A', border: '1px solid rgba(125,165,255,0.16)' }}>
                                <p className="text-[10px] font-medium uppercase tracking-wider mb-2" style={{ color: '#666' }}>Mensagem da equipe</p>
                                <p className="text-sm italic" style={{ color: '#ccc' }}>"{invite.welcome_message}"</p>
                            </div>
                        )}
                    </div>

                    {/* Right column — form */}
                    <div className="lg:col-span-3">
                        <form onSubmit={handleSubmit} className="rounded-2xl p-6" style={{ background: '#0B214A', border: '1px solid rgba(125,165,255,0.16)' }} data-testid="invite-form">
                            <h2 className="text-lg font-bold mb-1" style={{ color: '#F5F5F5' }}>Concluir cadastro</h2>
                            <p className="text-xs mb-5" style={{ color: '#666' }}>Preencha os dados abaixo para ativar sua empresa.</p>

                            {error && (
                                <div className="flex items-center gap-2 p-3 rounded-xl mb-4" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="invite-error">
                                    <AlertTriangle size={14} style={{ color: '#EF4444' }} />
                                    <span className="text-xs" style={{ color: '#EF4444' }}>{error}</span>
                                </div>
                            )}

                            {/* Empresa fields */}
                            <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#555' }}>Dados da empresa</p>
                            <div className="grid sm:grid-cols-2 gap-3 mb-5">
                                <div>
                                    <label className="text-[11px] block mb-1" style={{ color: '#888' }}>Nome da empresa *</label>
                                    <input type="text" value={form.empresa_name} onChange={e => setForm({ ...form, empresa_name: e.target.value })} placeholder="Ex.: Loja Exemplo LTDA"
                                        className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#071A3A', border: '1px solid rgba(125,165,255,0.16)', color: '#F5F5F5', outline: 'none' }} data-testid="inv-empresa-name" />
                                </div>
                                <div>
                                    <label className="text-[11px] block mb-1" style={{ color: '#888' }}>Nome fantasia</label>
                                    <input type="text" value={form.nome_fantasia} onChange={e => setForm({ ...form, nome_fantasia: e.target.value })} placeholder="Opcional"
                                        className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#071A3A', border: '1px solid rgba(125,165,255,0.16)', color: '#F5F5F5', outline: 'none' }} data-testid="inv-nome-fantasia" />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="text-[11px] block mb-1" style={{ color: '#888' }}>Email principal da empresa</label>
                                    <input type="email" value={form.empresa_email} onChange={e => setForm({ ...form, empresa_email: e.target.value })} placeholder="contato@empresa.com"
                                        className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#071A3A', border: '1px solid rgba(125,165,255,0.16)', color: '#F5F5F5', outline: 'none' }} data-testid="inv-empresa-email" />
                                </div>
                            </div>

                            {/* Owner fields */}
                            <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#555' }}>Responsavel da conta</p>
                            <div className="grid sm:grid-cols-2 gap-3 mb-5">
                                <div>
                                    <label className="text-[11px] block mb-1" style={{ color: '#888' }}>Nome do responsavel *</label>
                                    <input type="text" value={form.owner_name} onChange={e => setForm({ ...form, owner_name: e.target.value })} placeholder="Ex.: Joao Silva"
                                        className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#071A3A', border: '1px solid rgba(125,165,255,0.16)', color: '#F5F5F5', outline: 'none' }} data-testid="inv-owner-name" />
                                </div>
                                <div>
                                    <label className="text-[11px] block mb-1" style={{ color: '#888' }}>Email de acesso *</label>
                                    <input type="email" value={form.owner_email} onChange={e => setForm({ ...form, owner_email: e.target.value })} placeholder="joao@empresa.com"
                                        className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#071A3A', border: '1px solid rgba(125,165,255,0.16)', color: '#F5F5F5', outline: 'none' }} data-testid="inv-owner-email" />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="text-[11px] block mb-1" style={{ color: '#888' }}>WhatsApp do responsavel</label>
                                    <input type="tel" value={form.owner_phone} onChange={e => setForm({ ...form, owner_phone: e.target.value })} placeholder="(65) 99999-9999"
                                        className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#071A3A', border: '1px solid rgba(125,165,255,0.16)', color: '#F5F5F5', outline: 'none' }} data-testid="inv-owner-phone" />
                                </div>
                            </div>

                            {/* Password */}
                            <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#555' }}>Senha de acesso</p>
                            <div className="grid sm:grid-cols-2 gap-3 mb-5">
                                <div className="relative">
                                    <label className="text-[11px] block mb-1" style={{ color: '#888' }}>Criar senha *</label>
                                    <input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Min. 6 caracteres"
                                        className="w-full px-3 py-2.5 rounded-xl text-sm pr-10" style={{ background: '#071A3A', border: '1px solid rgba(125,165,255,0.16)', color: '#F5F5F5', outline: 'none' }} data-testid="inv-password" />
                                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-7" style={{ color: '#555' }}>
                                        {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                                    </button>
                                </div>
                                <div>
                                    <label className="text-[11px] block mb-1" style={{ color: '#888' }}>Confirmar senha *</label>
                                    <input type="password" value={form.password_confirm} onChange={e => setForm({ ...form, password_confirm: e.target.value })} placeholder="Repetir senha"
                                        className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#071A3A', border: '1px solid rgba(125,165,255,0.16)', color: '#F5F5F5', outline: 'none' }} data-testid="inv-password-confirm" />
                                </div>
                            </div>

                            {/* Summary */}
                            <div className="rounded-xl p-4 mb-5 space-y-2" style={{ background: '#071A3A', border: '1px solid rgba(125,165,255,0.16)' }}>
                                <div className="flex justify-between text-xs"><span style={{ color: '#666' }}>Plano</span><span className="font-semibold" style={{ color: planColor }}>{invite?.plan_name}</span></div>
                                <div className="flex justify-between text-xs"><span style={{ color: '#666' }}>Cobranca</span><span style={{ color: '#10B981' }}>Nao havera cobranca neste cadastro</span></div>
                                <div className="flex justify-between text-xs"><span style={{ color: '#666' }}>Origem</span><span style={{ color: '#888' }}>Acesso liberado por convite</span></div>
                                <div className="flex justify-between text-xs"><span style={{ color: '#666' }}>Convite</span><span style={{ color: '#888' }}>Uso unico</span></div>
                            </div>

                            {/* Checkboxes */}
                            <div className="space-y-2 mb-5">
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} className="mt-1 rounded" data-testid="inv-agree" />
                                    <span className="text-xs" style={{ color: '#888' }}>Declaro que sou responsavel por esta empresa e concordo com a criacao da conta.</span>
                                </label>
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input type="checkbox" checked={newsletter} onChange={e => setNewsletter(e.target.checked)} className="mt-1 rounded" />
                                    <span className="text-xs" style={{ color: '#666' }}>Quero receber comunicacoes importantes da SuperVitrine.</span>
                                </label>
                            </div>

                            {/* Submit */}
                            <button type="submit" disabled={submitting} className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-opacity"
                                style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)', opacity: submitting ? 0.6 : 1 }} data-testid="inv-submit">
                                {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                                Ativar empresa agora
                            </button>
                            <p className="text-center text-[10px] mt-2" style={{ color: '#555' }}>
                                Ao concluir, seu acesso sera ativado e este convite sera encerrado.
                            </p>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
